import { STORAGE_KEYS, YMS_API_CONFIG } from '@/constants';
import { ACTION_TYPES, taskcontants, TaskSectionType } from '@/constants/taskConstants';
import { convertCurrentTimeToPSTFormat, formatToDateTimeString } from '@/helper';
import { getInProgressTaskStorage } from '@/hooks/dashboard/offlineTaskQueue';
import { store } from '@/store/store';
import { formatPSTDate } from '@/utils';
import {
  mapOICTask,
  mapOICLocation,
  type OICTaskDetail,
  type OICLocationDetail,
} from '@/utils/oicMappers';

import { mapUpdateTaskRes } from './AuthenticationService';
import {
  GetTaskResponseDTO,
  mapTaskListRes,
  mapTaskSummaryRes,
  TaskSummaryRequestDTO,
  TaskSummaryResponseDTO,
  CreateAuditResponse,
  CreateAuditTaskRequestModel,
  mapUpdateTask,
  TaskSummaryModel,
} from './types/audittasktypes';
import {
  UpdateTaskFinalResponse,
  UpdateTaskRequest,
  UpdateTaskRequestUpdateTaskDetail,
  UpdateTaskResponse,
} from './types/authenticatetypes';
import { RequestTraceData } from './types/dockDoorTypes';

import type { GetTrailerModel, MoveLocationDetail } from './types/movetasktypes';
import type {
  AppSettings,
  ConfigurationModel,
  TaskDetail,
  UpdateTaskRequestModel,
  UpdateTaskResponseModel,
  GetTaskResponseDetailsModel,
} from '@/types';

// ─────────────────────────────────────────────────────────────────
// Settings resolver — survives page refresh even if Redux store lags
// ─────────────────────────────────────────────────────────────────

const VERIFY_DATA_KEY = 'yms_verify_data';

/**
 * Resolve AppSettings with fallback to persisted storage.
 * Priority: Redux store → localStorage (bootstrap config) → sessionStorage (verify OTP).
 * Handles the case where getAccessToken() fires before the Redux store is fully hydrated.
 */
function resolveSettings(): AppSettings | null {
  // 1. Primary: Redux store
  const storeSettings = store.getState().global.settings;
  if (storeSettings?.MA_ACCESS_REQUEST) return storeSettings;

  // 2. Fallback: localStorage (bootstrap config — persisted by setBootstrapConfig)
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.globalConfig);
    if (raw) {
      const config = JSON.parse(raw) as ConfigurationModel;
      if (config.settings.MA_ACCESS_REQUEST) return config.settings;
    }
  } catch {
    // Fall through
  }

  // 3. Fallback: localStorage/sessionStorage (verify OTP data)
  try {
    const verifyRaw =
      localStorage.getItem(VERIFY_DATA_KEY) ?? sessionStorage.getItem(VERIFY_DATA_KEY);
    if (verifyRaw) {
      const verifyData = JSON.parse(verifyRaw) as { settings: AppSettings };
      if (verifyData.settings.MA_ACCESS_REQUEST) return verifyData.settings;
    }
  } catch {
    // Fall through
  }

  // Return whatever store has (may be null)
  return storeSettings;
}

// ─────────────────────────────────────────────────────────────────
// Task Section Config — Matches Xamarin AppConstants
// ─────────────────────────────────────────────────────────────────

export type TaskSectionType = 'upcoming' | 'myTasks' | 'completed' | 'inProgress';

const FILTER_STRINGS: Record<TaskSectionType, string | ((userId: string) => string)> = {
  upcoming: 'IT.STATUS = 20',
  myTasks: (userId: string) => `IT.STATUS IN (30) AND IT.ASSIGNED_USER_ID = '${userId}'`,
  completed: (userId: string) => `IT.STATUS = 60 AND IT.ASSIGNED_USER_ID = '${userId}'`,
  inProgress: (userId: string) => `IT.STATUS = 40 AND IT.ASSIGNED_USER_ID = '${userId}'`,
};

const SORT_ORDERS: Record<TaskSectionType, string> = {
  upcoming: 'TASK_TYPE DESC, PRIORITY ASC',
  myTasks: 'TASK_TYPE DESC, PRIORITY ASC',
  completed: 'STATUS DESC, LAST_UPDATED_DTTM DESC',
  inProgress: 'PRIORITY ASC, TASK_ID DESC',
};

// ─────────────────────────────────────────────────────────────────
// OIC Base URL helper
// ─────────────────────────────────────────────────────────────────

export function getOICBaseUrl(): string {
  // DEV: Vite proxy handles CORS. PROD: Azure Function proxy avoids browser CORS.
  return import.meta.env.DEV ? '/middleware_new/' : '/api/oic-proxy/';
}

/**
 * Fetch wrapper for OIC calls. In production, Azure Static Web Apps intercepts
 * the Authorization header for its own auth system (returning 401). This wrapper
 * moves Authorization → X-Proxy-Authorization so it passes through to the Azure
 * Function, which converts it back before forwarding to OIC.
 */
export function oicFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);

  // In production, Azure Static Web Apps intercepts the Authorization header.
  // Move it to X-Proxy-Authorization so it passes through to the Azure Function.
  if (!import.meta.env.DEV) {
    const auth = headers.get('Authorization');
    if (auth) {
      headers.delete('Authorization');
      headers.set('X-Proxy-Authorization', auth);
    }
  }

  return fetch(url, { ...init, headers });
}

// ─────────────────────────────────────────────────────────────────
// Access Token (with dedup + TTL — matches Xamarin GetAccessToken)
// ─────────────────────────────────────────────────────────────────

// Matches Xamarin AccessTokenRequestDTO.cs — wire JSON casing (payload lowercase,
// Body/Request PascalCase, username/password lowercase).
interface MAAccessRequestBody {
  payload?: {
    Body?: {
      Request?: {
        username?: string;
        password?: string;
      };
    };
  };
}

let cachedToken: string | null = null;
let cachedTokenExpiry: number | null = null;
let tokenPromise: Promise<string> | null = null;

/** Clear cached token (for retry on 401) */
export function clearCachedToken(): void {
  cachedToken = null;
  cachedTokenExpiry = null;
}

export async function getAccessToken(): Promise<string> {
  if (cachedToken && cachedTokenExpiry && Date.now() < cachedTokenExpiry - 2 * 60 * 1000) {
    return cachedToken;
  }

  // Clear stale token
  cachedToken = null;
  cachedTokenExpiry = null;

  // Dedup: if a fetch is already in flight, reuse it
  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = (async () => {
    const settings = resolveSettings();
    const maAccessRequest = settings?.MA_ACCESS_REQUEST;
    if (!maAccessRequest) {
      throw new Error('MA_ACCESS_REQUEST is not available. Settings may not be loaded yet.');
    }
    const requestBody = JSON.parse(maAccessRequest) as MAAccessRequestBody;
    const username = requestBody.payload?.Body?.Request?.username ?? '';
    const password = requestBody.payload?.Body?.Request?.password ?? '';

    const baseUrl = getOICBaseUrl();

    const response = await oicFetch(`${baseUrl}${YMS_API_CONFIG.MAAccessTokenAPI}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${btoa(`${username}:${password}`)}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`MA token request failed: ${String(response.status)} ${response.statusText}`);
    }

    const tokenData = (await response.json()) as {
      TOKEN: string;
      TOKEN_TYPE: string;
      EXPIRES_IN: string;
    };
    cachedToken = tokenData.TOKEN;

    // Set expiry from EXPIRES_IN (seconds) — matches Xamarin DateTime.Now.AddSeconds(expirationTime)
    const expiresInSeconds = parseInt(tokenData.EXPIRES_IN, 10);
    if (!isNaN(expiresInSeconds) && expiresInSeconds > 0) {
      cachedTokenExpiry = Date.now() + expiresInSeconds * 1000;
    }

    return cachedToken;
  })();

  try {
    return await tokenPromise;
  } finally {
    tokenPromise = null;
  }
}

// ─────────────────────────────────────────────────────────────────
// Auth header builder — matches Xamarin AppMAAuthType switch
// ─────────────────────────────────────────────────────────────────

/**
 * Build Authorization header based on APP_MA_AUTH_TYPE config setting.
 * Xamarin switches between Bearer and Basic per-request:
 * - "bearer" → getAccessToken() then Authorization: Bearer {token}
 * - "basic"  → Authorization: Basic {username:password} directly
 */
export async function getAuthHeader(): Promise<string> {
  const settings = resolveSettings();
  const authType = settings?.APP_MA_AUTH_TYPE ?? 'bearer';

  if (authType === 'basic') {
    const accessRequestBody = JSON.parse(
      settings?.MA_ACCESS_REQUEST ?? '{}'
    ) as MAAccessRequestBody;
    const username = accessRequestBody.payload?.Body?.Request?.username ?? '';
    const password = accessRequestBody.payload?.Body?.Request?.password ?? '';
    return `Basic ${btoa(`${username}:${password}`)}`;
  }

  // Default: bearer
  const token = await getAccessToken();
  return `Bearer ${token}`;
}

// ─────────────────────────────────────────────────────────────────
// TraceData builder — matches Xamarin YMSHelperMethods.GetRequestTraceDataModel()
// ─────────────────────────────────────────────────────────────────

interface TraceData {
  User: string;
  Plant: string;
  SessionId: string;
}

function getTraceData(): TraceData {
  const { user, plant, sessionId } = store.getState().user;
  return {
    User: user !== null ? user._id : '',
    Plant: plant !== null ? plant._id : '',
    SessionId: sessionId ?? '',
  };
}

// ─────────────────────────────────────────────────────────────────
// Task List API — Matches Xamarin MiddlewareService.GetTaskListAsync
// ─────────────────────────────────────────────────────────────────

export async function getTaskList(
  section: TaskSectionType,
  orgId: string,
  userId?: string
): Promise<TaskDetail[]> {
  const baseUrl = getOICBaseUrl();

  if (!baseUrl) {
    throw new Error('MA_API_BASE_URL is not configured');
  }

  const filterDef = FILTER_STRINGS[section];
  const filterString = typeof filterDef === 'function' ? filterDef(userId ?? '') : filterDef;
  const sortOrder = SORT_ORDERS[section];

  const request = {
    payload: {
      TransmissionHeader: {
        TransmissionDateTime: formatPSTDate('date'),
        OriginatingSystem: 'YMSApp',
        TransmissionUser: 'nblyms',
        RoutingCode: 'MA',
        MessageType: 'GetTasks',
        MessageId: 'NA',
      },
      Body: {
        Org_id: orgId,
        Response_record_limit: 1000,
        Task_type: null,
        Filter_string: filterString,
        Sort_order: sortOrder,
      },
    },
    TraceData: getTraceData(),
  };

  const authHeader = await getAuthHeader();

  const response = await oicFetch(`${baseUrl}${YMS_API_CONFIG.MAGetTaskListAPI}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authHeader,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`OIC API error: ${String(response.status)} ${response.statusText}`);
  }

  const createAuditTaskRequest = (await response.json()) as Record<string, unknown>;

  // OIC response path: payload.Body.Task.Task_details[]
  const payload = (createAuditTaskRequest['Payload'] ?? createAuditTaskRequest['payload']) as
    | Record<string, unknown>
    | undefined;
  const body = payload?.['Body'] as Record<string, unknown> | undefined;
  const task = body?.['Task'] as Record<string, unknown> | undefined;
  const rawDetails = task?.['Task_details'] as OICTaskDetail[] | undefined;

  return rawDetails?.map(mapOICTask) ?? [];
}

// ─────────────────────────────────────────────────────────────────
// Update Task API — Matches Xamarin MiddlewareService.UpdateTaskAsync
// Endpoint: NBL_YMS_UPDAT_TASK_DTLS_MA/1.0/updateTask
// ─────────────────────────────────────────────────────────────────

/**
 * Transform internal PascalCase UpdateTaskRequestModel to OIC's field names.
 * Matches Xamarin UpdateTaskRequestDTO [JsonProperty] annotations exactly.
 */
function toOICUpdateRequest(request: UpdateTaskRequestModel): Record<string, unknown> {
  return {
    payload: {
      TransmissionHeader: request.Payload.TransmissionHeader,
      Body: {
        Org_id: request.Payload.Body.OrgId,
        Stop_on_first_action_error: request.Payload.Body.StopOnFirstActionError,
        Update_task_details: request.Payload.Body.UpdateTaskDetails.map((d) => ({
          Org_id: d.OrgId,
          Task_id: d.TaskId,
          Task_type: d.TaskType,
          User_id: d.UserId,
          Action_type: d.ActionType,
          Action_datetime: d.ActionDatetime,
          Trailer_id: d.TrailerId,
          Trailer_name: d.TrailerName,
          Carrier_company_name: d.CarrierCompanyName,
          Carrier_name: d.CarrierName,
          Exception_code: d.ExceptionCode ?? '',
          Task_comments: d.TaskComments,
          Trailer_comments: d.TrailerComments,
          Alt_Trailer_Valid: d.AlternateTrailerValid ?? '',
          Alt_Carrier: d.AlternateCarrier ?? '',
          Alt_Trailer: d.AlternateTrailerName ?? '',
          Alt_Dst_Zone: d.AlternateDestinationZone ?? '',
          Alt_Dst_Name: d.AlternateDestinationName ?? '',
          Override_reason_code: d.OverrideReasonCode ?? '',
        })),
      },
    },
    TraceData: request.TraceData,
  };
}

export async function updateTask(
  request: UpdateTaskRequestModel
): Promise<UpdateTaskResponseModel> {
  const baseUrl = getOICBaseUrl();

  if (!baseUrl) {
    throw new Error('MA_API_BASE_URL is not configured');
  }

  const authHeader = await getAuthHeader();
  const oicRequest = toOICUpdateRequest(request);

  const response = await oicFetch(`${baseUrl}${YMS_API_CONFIG.MAUpdateTaskAPI}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authHeader,
    },
    body: JSON.stringify(oicRequest),
  });

  if (!response.ok) {
    throw new Error(`OIC Update Task API error: ${String(response.status)} ${response.statusText}`);
  }

  // OIC returns HTTP 200 even for logical failures — check the body's Status field
  // Response path: payload.Body.Status / Status_message (matches Xamarin UpdateTaskResponseDTO)
  const raw = (await response.json()) as Record<string, unknown>;
  const payload = (raw['Payload'] ?? raw['payload']) as Record<string, unknown> | undefined;
  const body = payload?.['Body'] as Record<string, unknown> | undefined;
  const status = body?.['Status'] as string | undefined;
  const statusMessage = body?.['Status_message'] as string | undefined;

  if (status?.toLowerCase() === 'failed') {
    const updateDetails = body?.['Update_task_details'] as
      | { Status_message?: string }[]
      | undefined;
    const detailMessage = updateDetails?.[0]?.Status_message;
    throw new Error(detailMessage ?? statusMessage ?? 'Unknown error');
  }

  return raw as unknown as UpdateTaskResponseModel;
}

// ─────────────────────────────────────────────────────────────────
// Trailer List by Task ID — Matches Xamarin MiddlewareService.GetTrailerListByTaskIdAsync
// Endpoint: NBL_YMS_GET_TRAILERS/1.0/getTrailerDetails
// ─────────────────────────────────────────────────────────────────

interface OICTrailerResponse {
  Success: string;
  Message: string;
  Num_Records: number;
  Trailers?:
    | {
        Trailer_Name: string;
        Carrier_scac: string;
        Carrier_Name: string;
        Curr_Locn_Type: string;
        Curr_Locn_Zone: string;
        Curr_Locn_Name: string;
        Trailer_Comments: string;
      }[]
    | undefined;
}

export interface TrailerInfo {
  TrailerName: string;
  TrailerId: string;
  CarrierName: string;
  CarrierCompanyName: string;
}

export async function getTrailerListForTask(
  orgId: string,
  taskId: string,
  userId: string
): Promise<TrailerInfo[]> {
  const baseUrl = getOICBaseUrl();

  if (!baseUrl) {
    throw new Error('MA_API_BASE_URL is not configured');
  }

  // Matches Xamarin GetTrailerRequestDTO with [JsonProperty] field names
  const request = {
    size: null,
    org: orgId,
    user_id: userId,
    task_id: taskId,
    filter: '',
    sort: '',
    TraceData: getTraceData(),
  };

  const authHeader = await getAuthHeader();

  const response = await oicFetch(`${baseUrl}${YMS_API_CONFIG.MAGetTrailersAPI}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authHeader,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(
      `OIC Trailer List API error: ${String(response.status)} ${response.statusText}`
    );
  }

  const raw = (await response.json()) as OICTrailerResponse;

  return (raw.Trailers ?? []).map((t) => ({
    TrailerName: t.Trailer_Name,
    TrailerId: '', // OIC does not return TrailerId in this endpoint
    CarrierName: t.Carrier_scac,
    CarrierCompanyName: t.Carrier_Name,
  }));
}

// ─────────────────────────────────────────────────────────────────
// Location List by Task ID — Matches Xamarin MiddlewareService.GetLocationListByTaskIdAsync
// Endpoint: NBL_YMS_GET_LOCATION/1.0/getLocationDetails
// ─────────────────────────────────────────────────────────────────

interface OICLocationResponse {
  Success: string;
  Message: string;
  Num_Records: number;
  Locations?:
    | {
        Loc_Type: string;
        Loc_Zone: string;
        Loc_Name: string;
        Rank: string;
      }[]
    | undefined;
}

export interface LocationInfo {
  LocationName: string;
  LocationZone: string;
  LocationType: string;
}

export async function getLocationListForTask(
  orgId: string,
  taskId: string,
  userId: string
): Promise<LocationInfo[]> {
  const baseUrl = getOICBaseUrl();

  if (!baseUrl) {
    throw new Error('MA_API_BASE_URL is not configured');
  }

  // Matches Xamarin GetLocationRequestDTO with [JsonProperty] field names
  const request = {
    size: '100',
    org: orgId,
    user_id: userId,
    task_id: taskId,
    sort: 'RANK ASC',
    TraceData: getTraceData(),
  };

  const authHeader = await getAuthHeader();

  const response = await oicFetch(`${baseUrl}${YMS_API_CONFIG.MAGetLocationsAPI}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authHeader,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(
      `OIC Location List API error: ${String(response.status)} ${response.statusText}`
    );
  }

  const raw = (await response.json()) as OICLocationResponse;

  return (raw.Locations ?? []).map((l) => ({
    LocationName: l.Loc_Name,
    LocationZone: l.Loc_Zone,
    LocationType: l.Loc_Type,
  }));
}

// ─────────────────────────────────────────────────────────────────
// Location List API — Matches Xamarin GetLocationDetailList
// ─────────────────────────────────────────────────────────────────

export async function getLocationList(locationType: string): Promise<MoveLocationDetail[]> {
  const state = store.getState();
  const settings = state.global.settings;
  const baseUrl = getOICBaseUrl();
  const orgId = String(state.user.plant?.org_id ?? '');
  const userId = `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`;

  if (!baseUrl) {
    throw new Error('MA_API_BASE_URL is not configured');
  }

  const request = {
    payload: {
      TransmissionHeader: {
        TransmissionDateTime: formatPSTDate('date'),
        OriginatingSystem: 'YMSApp',
        TransmissionUser: 'nblyms',
        RoutingCode: 'MA',
        MessageType: 'getLocationList',
        MessageId: 'NA',
      },
      Body: {
        Org_id: orgId,
        Size: '',
        Sort: 'LOC_TYPE ASC, LOC_ZONE ASC, LOC_NAME',
        User_id: userId,
        Location_type: locationType,
      },
    },
    TraceData: getTraceData(),
  };

  const authHeader = await getAuthHeader();

  let customAuth = '';
  try {
    const raw = settings?.['MA_API_HEADERS'];
    if (raw && typeof raw === 'string') {
      const maApiHeaders = JSON.parse(raw) as Record<string, string>;
      const maUsername = maApiHeaders['username'] ?? '';
      const maPassword = maApiHeaders['password'] ?? '';
      if (maUsername && maPassword) {
        customAuth = `Basic ${btoa(`${maUsername}:${maPassword}`)}`;
      }
    }
  } catch {
    // MA_API_HEADERS is not valid JSON — fall through to standard auth
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: customAuth || authHeader,
  };

  const response = await oicFetch(`${baseUrl}${YMS_API_CONFIG.MAGetLocationsListAPI}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`OIC Location API error: ${String(response.status)} ${response.statusText}`);
  }

  const createAuditTaskRequest = (await response.json()) as Record<string, unknown>;

  const locations = createAuditTaskRequest['Locations'] as OICLocationDetail[] | undefined;

  return locations?.map(mapOICLocation) ?? [];
}

// ─────────────────────────────────────────────────────────────────
// Create Audit Tasks API
// ─────────────────────────────────────────────────────────────────

export async function CreateTasks(
  createAuditTaskRequest: CreateAuditTaskRequestModel
): Promise<CreateAuditResponse> {
  const token = await getAccessToken();
  const maBaseUrl = getOICBaseUrl();

  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MACreateAuditTasksAPI}`;
  const payloadrequest = mapUpdateTask(createAuditTaskRequest);

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payloadrequest),
  });

  if (!response.ok) {
    throw new Error(`CreateTasks API failed with status ${String(response.status)}`);
  }

  const data = (await response.json()) as CreateAuditResponse;
  return data;
}

export async function GetTrailerListAsync() {
  const state = store.getState();
  const orgId = String(state.user.plant?.org_id ?? '');
  const userId = `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`;
  const maBaseUrl = getOICBaseUrl();

  const getTrailerRequest = {
    payload: {
      TransmissionHeader: {
        TransmissionDateTime: formatPSTDate('date'),
        OriginatingSystem: 'YMSApp',
        TransmissionUser: 'nblyms',
        RoutingCode: 'MA',
        MessageType: 'getTrailerList',
        MessageId: 'NA',
      },
      Body: {
        Org_id: orgId,
        Filter: '',
        Size: '',
        Sort: 'CARRIER_SCAC ASC',
        User_id: userId,
      },
    },
  };

  const token = await getAccessToken();

  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MAGetTrailerListAPI}`;

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(getTrailerRequest),
  });

  if (!response.ok) {
    throw new Error(`Get Trailer list API failed with status ${String(response.status)}`);
  }

  const data = (await response.json()) as GetTrailerModel;
  return data.Trailers;
}

export async function GetTaskSummaryAsync(): Promise<TaskSummaryModel | null> {
  const state = store.getState();
  const orgId = String(state.user.plant?.org_id ?? '');
  const userId = `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`;

  const maBaseUrl = getOICBaseUrl();
  try {
    const summaryRequest: TaskSummaryRequestDTO = {
      Org: orgId,
      Start_time: formatToDateTimeString(state.user.user?.login_date_time ?? ''),
      End_time: convertCurrentTimeToPSTFormat(),
      User_id: userId,
    };

    const token = await getAccessToken();
    const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MASummaryAPI}`;

    const response = await oicFetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(summaryRequest),
    });
    if (!response.ok) {
      throw new Error(`Get TaskSummary API failed with status ${String(response.status)}`);
    }
    const taskSummaryResponse = (await response.json()) as TaskSummaryResponseDTO;
    if (taskSummaryResponse.payload.Body.Status) {
      return mapTaskSummaryRes(taskSummaryResponse);
    }
    return null;
  } catch (err) {
    throw new Error(String(err));
  }
}

export async function GetAllTaskToRelease(): Promise<UpdateTaskRequest> {
  const state = store.getState();
  const orgId = String(state.user.plant?.org_id ?? '');
  const userId = `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`;

  const myTaskListResponse = await GetTaskListAsync(TaskSectionType.MyTaskList);
  const acceptedTaskList = myTaskListResponse.Payload.Body.Task.TaskDetails;

  const inProgressTask = getInProgressTaskStorage();

  const releaseTasks: UpdateTaskRequestUpdateTaskDetail[] = acceptedTaskList.map((task) => ({
    Task_type: task.TaskType,
    Org_id: orgId,
    Task_id: task.TaskId,
    User_id: userId,
    Action_type: ACTION_TYPES.CANCEL,
    Action_datetime: convertCurrentTimeToPSTFormat(),
    Task_comments: ' ',
    Trailer_id: task.TrailerId,
    Trailer_name: task.TrailerName,
    Trailer_comments: ' ',
    Carrier_company_name: task.CarrierCompanyName,
    Carrier_name: task.CarrierName,
    Exception_code: '',
    Alt_Trailer_Valid: '',
    Alt_Carrier: '',
    Alt_Trailer: '',
    Alt_Dst_Zone: '',
    Alt_Dst_Name: '',
    Override_reason_code: '',
  }));

  // Add in-progress task if exists
  if (inProgressTask != null) {
    releaseTasks.push({
      Task_type: inProgressTask.TaskType,
      Org_id: orgId,
      Task_id: inProgressTask.TaskId,
      User_id: userId,
      Action_type: ACTION_TYPES.CANCEL,
      Action_datetime: convertCurrentTimeToPSTFormat(),
      Task_comments: ' ',
      Trailer_id: inProgressTask.TrailerId,
      Trailer_name: inProgressTask.TrailerName,
      Trailer_comments: ' ',
      Carrier_company_name: inProgressTask.CarrierCompanyName,
      Carrier_name: inProgressTask.CarrierName,
      Exception_code: '',
      Alt_Trailer_Valid: '',
      Alt_Carrier: '',
      Alt_Trailer: '',
      Alt_Dst_Zone: '',
      Alt_Dst_Name: '',
      Override_reason_code: '',
    });
  }

  return {
    payload: {
      TransmissionHeader: GetTransmissionHeader(),
      Body: {
        Org_id: orgId,
        Stop_on_first_action_error: taskcontants.StopOnFirstError,
        Update_task_details: releaseTasks,
      },
    },
    TraceData: getTraceDataNew(),
  };
}

export async function GetTaskListAsync(
  taskSectionType: string
): Promise<GetTaskResponseDetailsModel> {
  const maBaseUrl = getOICBaseUrl();
  const state = store.getState();
  const user = state.user.user;
  const orgId = String(state.user.plant?.org_id ?? '');
  const userId = `${user?.country_code ?? ''}${user?.mobile_no ?? ''}`;

  let sortOrder = ' ';
  let filterString = ' ';

  switch (taskSectionType) {
    case TaskSectionType.MyTaskList:
      sortOrder = taskcontants.SortOrderPriorityDescTaskIdAsc;
      filterString = taskcontants.FilterMyTaskTask.replace('{0}', userId);
      break;
    case TaskSectionType.UpcomingTaskList:
      sortOrder = taskcontants.SortOrderPriorityDescTaskIdAsc;
      filterString = taskcontants.FilterUpcomingTask;
      break;
    case TaskSectionType.CompletedTaskList:
      sortOrder = taskcontants.SortOrderPriorityStatusDescLastedUpdatedDesc;
      filterString = taskcontants.FilterCompletedTask.replace('{0}', userId);
      break;
    case TaskSectionType.OnGoingTask:
      sortOrder = taskcontants.SortOrderPriorityLastUpdatedFirst;
      filterString = taskcontants.FilterOnGoingTask.replace('{0}', userId);
      break;
    case TaskSectionType.AllTaskList:
      sortOrder = taskcontants.SortOrderPriorityStatusDescLastedUpdatedDesc;
      filterString = ' ';
      break;
  }

  const getTaskRequest = {
    payload: {
      TransmissionHeader: {
        TransmissionDateTime: convertCurrentTimeToPSTFormat(),
        OriginatingSystem: taskcontants.OriginatingSystem,
        TransmissionUser: taskcontants.TransmissionUser,
        RoutingCode: taskcontants.RoutingCode,
        MessageType: taskcontants.messageTypeGetTasks,
        MessageId: taskcontants.MessageId,
      },
      Body: {
        Org_id: orgId,
        Response_record_limit: 1000,
        Filter_string: filterString,
        Task_type: null,
        Sort_order: sortOrder,
      },
    },
    TraceData: getTraceDataNew(),
  };

  const token = await getAccessToken();
  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MAGetTaskListAPI}`;

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(getTaskRequest),
  });

  if (!response.ok) {
    throw new Error(`OIC API error: ${String(response.status)} ${response.statusText}`);
  }

  const taskSummaryResponse = (await response.json()) as GetTaskResponseDTO;
  const responseData: GetTaskResponseDetailsModel = mapTaskListRes(taskSummaryResponse);
  const myTaskFilter = taskcontants.FilterMyTaskTask.replace('{0}', userId);
  const completedFilter = taskcontants.FilterCompletedTask.replace('{0}', userId);

  if (filterString === myTaskFilter) {
    localStorage.setItem(STORAGE_KEYS.offlineMyTaskList, JSON.stringify(responseData));
  } else if (filterString === taskcontants.FilterUpcomingTask) {
    localStorage.setItem(STORAGE_KEYS.offlineUpcomingTask, JSON.stringify(responseData));
  } else if (filterString === completedFilter) {
    localStorage.setItem(STORAGE_KEYS.offlineCompletedTask, JSON.stringify(responseData));
  }

  return responseData;
}

export async function UpdateTaskAsync(
  updateTaskRequest: UpdateTaskRequest
): Promise<UpdateTaskFinalResponse> {
  const maBaseUrl = getOICBaseUrl();
  const token = await getAccessToken();
  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MAUpdateTaskAPI}`;

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updateTaskRequest),
  });

  const taskSummaryResponse = (await response.json()) as UpdateTaskResponse;
  return mapUpdateTaskRes(taskSummaryResponse);
}

export function GetTransmissionHeader() {
  return {
    TransmissionDateTime: convertCurrentTimeToPSTFormat(),
    OriginatingSystem: taskcontants.OriginatingSystem,
    TransmissionUser: taskcontants.TransmissionUser,
    RoutingCode: taskcontants.RoutingCode,
    MessageType: taskcontants.messageTypeUpdateTasks,
    MessageId: taskcontants.MessageId,
  };
}

export function getTraceDataNew(): RequestTraceData {
  const { user, plant, sessionId } = store.getState().user;
  return {
    User: user !== null ? user._id : '',
    Plant: plant !== null ? plant._id : '',
    SessionId: sessionId ?? '',
  };
}
